import {
  AppointmentTypeDto,
  CreateAppointmentTypeDto,
  SaveBookingQuestionsDto,
  UpdateAppointmentTypeDto,
} from '@shared/core/appointment-types'
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from 'react-query'
import {
  GET_CANCELLATION_POLICY_CACHE_KEY,
  GET_LATE_CANCEL_COUNTS_CACHE_KEY,
} from 'src/modules/cancellation-policy/constants/cancellation-policy-cache-keys'
import { useHandleRetry } from 'src/modules/core/hooks/use-handle-retry'
import { GET_BOOKING_ONBOARDING_CACHE_KEY } from 'src/modules/user/hooks/use-booking-onboarding'
import { ApiService } from 'src/services/api.service'

export const GET_APPOINTMENT_TYPES_CACHE_KEY = 'get-appointment-types'
export const GET_DEFAULT_APPOINTMENT_TYPE_CACHE_KEY = 'get-default-appointment-type'

export function useAppointmentTypes(userId: string, options?: UseQueryOptions<AppointmentTypeDto[]>) {
  const handleRetry = useHandleRetry()

  return useQuery([GET_APPOINTMENT_TYPES_CACHE_KEY], () => ApiService.AppointmentTypes.list(userId), {
    staleTime: 300000,
    retry: handleRetry,
    ...options,
  })
}

export function useDefaultAppointmentType(userId: string, options?: UseQueryOptions<AppointmentTypeDto | null>) {
  const handleRetry = useHandleRetry()

  return useQuery(
    [GET_DEFAULT_APPOINTMENT_TYPE_CACHE_KEY, userId],
    () => ApiService.AppointmentTypes.getDefault(userId),
    {
      staleTime: 300000,
      retry: handleRetry,
      enabled: !!userId,
      ...options,
    },
  )
}

export function useCreateAppointmentType() {
  const queryClient = useQueryClient()
  return useMutation((data: CreateAppointmentTypeDto) => ApiService.AppointmentTypes.create(data), {
    onSuccess: () => {
      queryClient.invalidateQueries([GET_APPOINTMENT_TYPES_CACHE_KEY])
      queryClient.invalidateQueries([GET_BOOKING_ONBOARDING_CACHE_KEY])
      queryClient.invalidateQueries([GET_DEFAULT_APPOINTMENT_TYPE_CACHE_KEY])
      queryClient.invalidateQueries([GET_CANCELLATION_POLICY_CACHE_KEY])
      queryClient.invalidateQueries([GET_LATE_CANCEL_COUNTS_CACHE_KEY])
    },
  })
}

export function useUpdateAppointmentType() {
  const queryClient = useQueryClient()
  return useMutation(
    ({ id, data }: { id: string; data: UpdateAppointmentTypeDto }) => ApiService.AppointmentTypes.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_APPOINTMENT_TYPES_CACHE_KEY])
        queryClient.invalidateQueries([GET_BOOKING_ONBOARDING_CACHE_KEY])
        queryClient.invalidateQueries([GET_DEFAULT_APPOINTMENT_TYPE_CACHE_KEY])
        // The type modal reads its custom policy from the account policy, not from the type list.
        queryClient.invalidateQueries([GET_CANCELLATION_POLICY_CACHE_KEY])
        // A custom-mode type alone turns the Late cancels nav on.
        queryClient.invalidateQueries([GET_LATE_CANCEL_COUNTS_CACHE_KEY])
      },
    },
  )
}

export function useSaveBookingQuestions() {
  const queryClient = useQueryClient()
  return useMutation(
    ({ id, data }: { id: string; data: SaveBookingQuestionsDto }) =>
      ApiService.AppointmentTypes.saveBookingQuestions(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_APPOINTMENT_TYPES_CACHE_KEY])
        queryClient.invalidateQueries([GET_DEFAULT_APPOINTMENT_TYPE_CACHE_KEY])
      },
    },
  )
}

export function useDeleteAppointmentType() {
  const queryClient = useQueryClient()
  return useMutation((id: string) => ApiService.AppointmentTypes.delete(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([GET_APPOINTMENT_TYPES_CACHE_KEY])
      queryClient.invalidateQueries([GET_BOOKING_ONBOARDING_CACHE_KEY])
      queryClient.invalidateQueries([GET_DEFAULT_APPOINTMENT_TYPE_CACHE_KEY])
      queryClient.invalidateQueries([GET_CANCELLATION_POLICY_CACHE_KEY])
      queryClient.invalidateQueries([GET_LATE_CANCEL_COUNTS_CACHE_KEY])
    },
  })
}

export function useSetAsDefaultAppointmentType() {
  const queryClient = useQueryClient()
  return useMutation((id: string) => ApiService.AppointmentTypes.setAsDefault(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([GET_APPOINTMENT_TYPES_CACHE_KEY])
      queryClient.invalidateQueries([GET_DEFAULT_APPOINTMENT_TYPE_CACHE_KEY])
    },
  })
}
